
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { tryMultipleAssetPaths } from "@/utils/assetUtils";

const Navbar = () => {
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [logoPath, setLogoPath] = useState("");
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [signUpButtonPath, setSignUpButtonPath] = useState("");
  const [signUpButtonLoaded, setSignUpButtonLoaded] = useState(false);
  
  useEffect(() => {
    // Find a working path for the logo
    tryMultipleAssetPaths("disco_logo_v2.png", (path) => {
      if (path) {
        console.log("✅ Successfully loaded logo path:", path);
        setLogoPath(path);
      } else {
        console.error("Failed to load logo in Navbar");
      }
    });
    
    // Load sign up button asset
    tryMultipleAssetPaths("landing_sign_up_header_cta.png", (path) => {
      if (path) {
        console.log("✅ Successfully loaded sign up button path:", path);
        setSignUpButtonPath(path);
        setSignUpButtonLoaded(true);
      } else {
        console.error("Failed to load sign up button in Navbar");
      }
    });
  }, []);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  
  // Close mobile menu when route changes
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  return (
    <div className="fixed top-0 left-0 w-full z-50 bg-black/90 backdrop-blur-sm">
      <div className="container mx-auto px-6 py-4">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center z-10">
            {!logoLoaded && logoPath === "" && (
              <span className="text-2xl font-bold text-white">DISCO</span>
            )}
            
            {logoPath && (
              <img 
                src={logoPath} 
                alt="Disco" 
                className="h-10"
                onLoad={() => setLogoLoaded(true)}
                onError={() => {
                  console.error("Failed to load logo in Navbar");
                }}
              />
            )}
          </Link>

          {isMobile ? (
            <>
              <button
                onClick={toggleMenu}
                className="text-white p-2 focus:outline-none z-10"
                aria-label="Toggle menu"
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>

              {isMenuOpen && (
                <div className="fixed inset-0 top-[72px] bg-black flex flex-col items-center pt-10">
                  <div className="w-full h-full flex justify-center items-start pt-10">
                    <div className="bg-black/75 backdrop-blur-sm py-10 px-12 rounded-lg w-4/5 flex flex-col items-center space-y-8">
                      <Link
                        to="/login"
                        onClick={toggleMenu}
                        className="text-white text-xl hover:text-green-400 transition-colors"
                      >
                        Login
                      </Link>
                      <Link to="/register-new" onClick={toggleMenu} className="inline-block">
                        {signUpButtonPath ? (
                          <img 
                            src={signUpButtonPath} 
                            alt="Sign Up" 
                            className="h-12 hover:opacity-90 transition-opacity"
                          />
                        ) : (
                          <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
                            Sign Up
                          </Button>
                        )}
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center space-x-6">
              <Link to="/login" className="text-white hover:text-white/80">
                Login
              </Link>
              <Link to="/register-new" className="inline-block">
                {signUpButtonPath ? (
                  <img 
                    src={signUpButtonPath} 
                    alt="Sign Up" 
                    className="h-10 hover:opacity-90 transition-opacity"
                    onLoad={() => setSignUpButtonLoaded(true)}
                  />
                ) : (
                  <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
                    Sign Up
                  </Button>
                )}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Navbar;
